import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}


export const TIER_COLORS: Record<string, string> = {
  "Nano": "#ffb1b1",
  "Micro": "#8bbfff",
  "Micro Plus": "#1773e5",
  "Macro": "#fcd34D",
  "Macro Plus": "#c39500",
  "Mega": "#84f4c1",
  "Mega Plus": "#25a76c"
};


export const CONTENT_TYPE_COLORS: Record<string, string> = {
  'Photo': '#93c5fd',
  'Video': '#fda4af' ,
  'Carousel' : '#c084fc',
  'Reel': '#E6CD69' ,
  'Short': '#c084fc',
  'short': '#c084fc',
  'Image': '#fda4af',
  'image': '#fda4af',
  'photo': '#93c5fd',
  'video': '#fda4af' ,
  'carousel' : '#c084fc' ,
  'reel': '#E6CD69' ,
  'Male': '#3592fc',
  'Female': '#ff78b1',
  'Other': '#f3d55fff'
      
};

export const getContentTypeColor = (type: string = ''): string => {
  return CONTENT_TYPE_COLORS[type.toLowerCase()] || '#000000'; // fallback color
};

export const darkenHex = (hex: string, amount = 0.2): string => {
  if (!hex || typeof hex !== "string") {
    console.warn("darkenHex received invalid color:", hex);
    return "#000000"; // safe fallback
  }

  // Normalize & validate
  hex = hex.trim().replace("#", "").toLowerCase();
  if (!/^[0-9a-f]{6}$/.test(hex)) {
    console.warn("Invalid HEX:", hex);
    return "#000000";
  }

  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  r = Math.round(r * (1 - amount));
  g = Math.round(g * (1 - amount));
  b = Math.round(b * (1 - amount));

  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};  
  

export const getTierBadgeStyle = (
  tier: string,
  colorMap: Record<string, string>
) => {
  const color = colorMap[tier] ?? "#cccccc";
  const borderCol = darkenHex(color, 0.35);
  let textCol = darkenHex(color, 0.65);
  // Special overrides
  if (tier?.toLowerCase() === "mega plus") {
    textCol = "#97ffd1";
  }
  if (tier?.toLowerCase() === "macro plus") {
    textCol = "#fff0bf";
  }
  // #fff0bf
// #97ffd1
  return {
    backgroundColor: color,
    border: `1px solid ${borderCol}`,
    color: textCol,
    padding: "2px 6px",
    borderRadius: "4px",  
    fontSize: "12px",
  } as React.CSSProperties;
};

// utils/numberFormat.ts
export const formatNumberIN = (v: number | string | null | undefined) => {
  if (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) return "--";
  return Number(v).toLocaleString("en-IN");
};

/**
 * Short formatter (e.g. 150000 -> "1.5L", 2500000 -> "2.5M")
 * You can tweak thresholds and suffixes as desired.
 */
export const formatShort = (
  v: number | string | null | undefined,
  precision = 1
) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "--";
  const n = Number(v);
  const abs = Math.abs(n);

  // Crore (>= 1,00,00,000)
  if (abs >= 1_00_00_000) {
    return `${(n / 1_00_00_000)
      .toFixed(precision)
      .replace(/\.0+$/, "")
      .replace(/(\.\d*[1-9])0+$/, "$1")}Cr`;
  }

  // Million (>= 1,000,000) — prefer 'M' for international million
  if (abs >= 1_000_000) {
    return `${(n / 1_000_000)
      .toFixed(precision)
      .replace(/\.0+$/, "")
      .replace(/(\.\d*[1-9])0+$/, "$1")}M`;
  }

  // Lakh (>= 1,00,000)
  if (abs >= 1_00_000) {
    return `${(n / 1_00_000)
      .toFixed(precision)
      .replace(/\.0+$/, "")
      .replace(/(\.\d*[1-9])0+$/, "$1")}L`;
  }

  // fallback: small number — show raw without suffix
  return `${n}`;
};

export const formatBoth = (v: number | string | null | undefined) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "--";

  const full = formatNumberIN(v); // ex: 1,23,456
  const short = formatShort(v); // ex: 1.23L

  return `${full} (${short})`;
};